import { useCallback } from "react";
import { toast } from "sonner";

import { keys } from "@/hooks/queries";
import { trackJob } from "@/hooks/use-upload";
import { api, queryClient, type DocumentOut } from "@/lib/api";
import { useActivity } from "@/stores/activity";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

export function useWebIngest() {
  return useCallback(async (url: string): Promise<boolean> => {
    const value = url.trim();
    if (!value) return false;
    try {
      const { document, job_id, deduplicated } = await api.request<{
        document: DocumentOut;
        job_id: string | null;
        deduplicated: boolean;
      }>("POST", "/api/documents/web", { url: value });
      if (deduplicated || !job_id) {
        toast.info(`${document.title} is already in the atlas`);
        useTabs.getState().openReader(document.id, document.title);
        return true;
      }
      useActivity.getState().upsert({
        id: job_id,
        kind: "ingest",
        title: document.title,
        docId: document.id,
        status: "queued",
      });
      toast(`Reading ${document.title}…`, {
        description: "Extracting the article, metadata, concepts and connections.",
      });
      const job = await trackJob(job_id, document.id);
      if (job.status !== "done") {
        toast.error(`Failed to ingest ${document.title}`, { description: job.error ?? "" });
        return false;
      }
      toast.success(`${document.title} joined the atlas`);
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      await queryClient.invalidateQueries({ queryKey: keys.documents });
      const overview = await queryClient.fetchQuery({
        queryKey: keys.overview,
        queryFn: () => api.graphOverview(),
      });
      const docNode = (
        overview as { document_nodes: { id: string; document_id: string | null }[] }
      ).document_nodes.find((node) => node.document_id === document.id);
      if (docNode) {
        useTabs.getState().activate("graph");
        useGraph.getState().markFlash([docNode.id]);
        useGraph.getState().requestFocus(docNode.id);
      }
      const enrichJobId = (job.result as { enrich_job_id?: string } | null)?.enrich_job_id;
      if (enrichJobId) {
        void trackJob(enrichJobId).then(() =>
          queryClient.invalidateQueries({ queryKey: keys.overview }),
        );
      }
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      // A 405 / network failure almost always means a stale or stopped backend.
      const stale = /\b405\b|Method Not Allowed|Failed to fetch|NetworkError|ECONNREFUSED/i.test(msg);
      toast.error("Website import failed", {
        description: stale
          ? `${msg} — the backend looks out of date or stopped; restart "python -m graphmind serve".`
          : msg,
      });
      return false;
    }
  }, []);
}
